// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';
import {fetchEffectData} from 'Utils/react-utils';
import {exportWorkboardReport} from 'Services/excel-service';
import {IconWdt} from '@deere/icons';
import {getJobsBetweenDates} from 'Services/job-service';
import {fetchAndFilterInactiveOperators} from 'OnLabor/workboard/utils/fetch-operators';
import {formatTime} from 'Utils/time-utils';
import moment from 'moment';
import {getFormattedJobs} from 'OnLabor/report/utils/workboard-report-util';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {THIRTY_DAYS} from 'Ui/constants/time-constants';
import {VIEW_WORKBOARD_SUMMARY_DATA} from 'Common/constants/business-activities';
import {PRO_GOLF_LABOR_REPORTS_1} from 'Common/constants/isg-toggles';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {ColumnHelpers, DateRangeChip} from '@deere/isg.component-library';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getWorkboardJobListExportContent} from 'Utils/labor-report-export-utils';

const SEVEN_DAYS = 7;
const DEFAULT_LAST_30_DAYS = 7;

const DEFAULT_DATE_RANGE = {
    startDate: moment().startOf('day').subtract(THIRTY_DAYS, 'days'),
    endDate: moment().endOf('day')
};

const TODAY = moment().endOf('day').toDate();

function getDateRangeOptions(translations) {
    return [
        {
            id: 2,
            title: translations.days_Today,
            getSelectedDates: () => {
                return [moment().startOf('day').toDate(), TODAY];
            }
        },
        {
            id: 3,
            title: translations.YESTERDAY,
            getSelectedDates: () => {
                return [moment().startOf('day').subtract(1, 'days').toDate(), moment().endOf('day').subtract(1, 'days').toDate()];
            }
        },
        {
            id: 4,
            title: translations.THIS_WEEK,
            getSelectedDates: () => {
                return [moment().startOf('week').toDate(), moment().endOf('week').toDate()];
            }
        },
        {
            id: 5,
            title: translations.LAST_WEEK,
            getSelectedDates: () => {
                return [moment().subtract(1, 'weeks').startOf('week').toDate(), moment().subtract(1, 'weeks').endOf('week').toDate()];
            }
        },
        {
            id: 6,
            title: translations.LAST_7_DAYS,
            getSelectedDates: () => {
                return [moment().startOf('day').subtract(SEVEN_DAYS, 'days').toDate(), TODAY];
            }
        },
        {
            id: DEFAULT_LAST_30_DAYS,
            title: translations.LAST_30_DAYS,
            getSelectedDates: () => {
                return [moment().startOf('day').subtract(THIRTY_DAYS, 'days').toDate(), TODAY];
            }
        },
        {
            id: 8,
            title: translations.THIS_MONTH,
            getSelectedDates: () => {
                return [moment().startOf('month').toDate(), moment().endOf('month').toDate()];
            }
        },
        {
            id: 9,
            title: translations.LAST_MONTH,
            getSelectedDates: () => {
                return [moment().subtract(1, 'months').startOf('month').toDate(), moment().subtract(1, 'months').endOf('month').toDate()];
            }
        }
    ];
}

function getColumns(translations) {
    return [
        {
            headerName: translations.OPERATOR,
            field: 'operator',
            minWidth: 200,
            flex: 1
        },
        ColumnHelpers.localizedDateColumn({
            field: 'dateSelect',
            headerName: translations.DATE
        }, {
            minWidth: 150,
            flex: 1
        }),
        {
            headerName: translations.ONLINK_WORKBOARD,
            field: 'workboardTitle',
            minWidth: 200,
            flex: 1
        },
        {
            headerName: translations.jobs_job,
            field: 'title',
            minWidth: 200,
            flex: 1
        },
        {
            headerName: translations.EQUIPMENT_LIST,
            field: 'equipmentNames',
            minWidth: 250,
            flex: 1
        },
        {
            headerName: translations.ONLINK_ROUTE,
            field: 'route',
            minWidth: 100,
            flex: 1
        },
        {
            headerName: translations.ONLINK_MOWING_DIRECTIONS,
            field: 'mowingDirection',
            minWidth: 200,
            flex: 1
        },
        {
            headerName: translations.ONLINK_EST_HRS,
            field: 'estDuration',
            minWidth: 120,
            type: 'number',
            flex: 1
        },
        {
            headerName: translations.ONLINK_ACT_HRS,
            field: 'actualDuration',
            minWidth: 120,
            type: 'number',
            flex: 1
        },
        {
            headerName: translations.ONLINK_PRE_START,
            field: 'formattedPreStartCheck',
            minWidth: 120,
            flex: 1
        },
        {
            headerName: translations.NOTE,
            field: 'note',
            minWidth: 250,
            flex: 1
        }
    ];
}

function setDateRangeFromChip(dates, setDateRange) {
    const [startValue, endValue] = dates;

    if (startValue && endValue) {
        setDateRange({
            startDate: moment(startValue),
            endDate: moment(endValue)
        });
    } else {
        setDateRange(DEFAULT_DATE_RANGE); // Defaults to last 30 days if the user clicks "Reset"
    }
}

function getAdditionalActionButtons(translations, dateRange, setDateRange) {
    const dateRangeOptions = getDateRangeOptions(translations);
    const selectedDates = [dateRange.startDate.toDate(), dateRange.endDate.toDate()];

    return (
        <div className='extra-table-options'>

            <DateRangeChip
                dateRangePresets={dateRangeOptions}
                isPersistent
                onChange={(newDates) => {
                    setDateRangeFromChip(newDates, setDateRange);
                }}
                value={selectedDates}
            />
        </div>
    );
}

function initializeState() {
    const [workboardReport, setWorkboardReport] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => true);
    const [dateRange, setDateRange] = React.useState(DEFAULT_DATE_RANGE);

    return {
        workboardReport,
        setWorkboardReport,
        loading,
        setLoading,
        dateRange,
        setDateRange
    };
}

function useWorkboardReportExport(workboardReport, dateRange, translations, addToast, isExportPreviewEnabled) {
    const [showExportDialog, setShowExportDialog] = React.useState(false);
    const exportContent = React.useMemo(() => getWorkboardJobListExportContent(workboardReport, translations), [workboardReport, translations]);
    const selectedTimeframe = `${dateRange.startDate.format('MMM D, YYYY')} - ${dateRange.endDate.format('MMM D, YYYY')}`;

    const handleClose = () => setShowExportDialog(false);
    const handleDownload = React.useCallback(async () => {
        await exportWorkboardReport(workboardReport, translations, addToast);
        setShowExportDialog(false);
    }, [workboardReport, translations, addToast]);
    const handleOpen = React.useCallback(async () => {
        if (isExportPreviewEnabled) {
            setShowExportDialog(true);
        } else {
            await handleDownload();
        }
    }, [handleDownload, isExportPreviewEnabled]);

    return {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        selectedTimeframe,
        showExportDialog
    };
}

function WorkboardReport(props) {
    const {
        membershipId,
        translations,
        isMigrated,
        isExportPreviewEnabled,
        myJdPermissions,
        orgId
    } = props;

    const {
        workboardReport,
        setWorkboardReport,
        loading,
        setLoading,
        dateRange,
        setDateRange
    } = initializeState();

    const {addToast} = useToast();

    const hasExportDataPermission = React.useMemo(() => isAuthorized({
        myJdPermissions: VIEW_WORKBOARD_SUMMARY_DATA
    }, {
        myJdPermissions,
        isMigrated
    }), [isMigrated, myJdPermissions]);
    const {
        exportContent,
        handleClose,
        handleDownload,
        handleOpen,
        selectedTimeframe,
        showExportDialog
    } = useWorkboardReportExport(
        workboardReport,
        dateRange,
        translations,
        addToast,
        Boolean(isExportPreviewEnabled)
    );

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const endDate = formatTime(dateRange.endDate, 'yyyyMMDD');
        const startDate = formatTime(dateRange.startDate, 'yyyyMMDD');
        const {jobList} = await getJobsBetweenDates(startDate, endDate);
        const teamMembers = await fetchAndFilterInactiveOperators(orgId);

        if (isMounted()) {
            const workboardReportData = getFormattedJobs(jobList, translations, teamMembers);

            setWorkboardReport(workboardReportData);
            setLoading(false);
        }
    }), [membershipId, dateRange]);

    useNavBarActions(hasExportDataPermission ? [{
        Icon: <IconWdt/>,
        onClick: handleOpen,
        title: 'EXPORT',
        variant: 'primary'
    }] : []);

    return (
        <>
            <div className={'ops-center-table-container'}>
                <DataGridWrapper
                    columns={getColumns(translations)}
                    filterComponent={getAdditionalActionButtons(translations, dateRange, setDateRange)}
                    initialState={{
                        sorting: {
                            sortModel: [{
                                field: 'dateSelect',
                                sort: 'desc'
                            }]
                        }
                    }}
                    loading={loading}
                    pinnedColumns={{
                        left: [],
                        right: []
                    }}
                    rows={workboardReport}
                    searchFields={['operator', 'workboardTitle', 'title', 'equipmentNames', 'route', 'mowingDirection', 'note']}
                    searchable={true}
                    showDeleteColumn={false}
                    slotProps={{
                        root: {
                            disableColumnMenu: true
                        }
                    }}
                    translations={translations}
                />
            </div>
            {
                showExportDialog &&
                <LaborReportExportDialog
                    dateRange={selectedTimeframe}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

WorkboardReport.propTypes = {
    isExportPreviewEnabled: PropTypes.bool,
    isMigrated: PropTypes.bool,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    orgId: PropTypes.number,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membershipId: state.membership.membershipId,
        isMigrated: state.membership.isMigrated,
        isExportPreviewEnabled: Boolean(state.account.isgToggles?.[PRO_GOLF_LABOR_REPORTS_1]),
        myJdPermissions: state.account.myJdPermissions,
        orgId: state.membership.orgId
    };
}

export default connect(mapStateToProps)(withRouter(WorkboardReport));
